import {getTestProps} from '@benchling/js-test-utils';
import {Tag, TagColor, TagSize} from '@benchling/nucleus-ui';
import {StyleSheet, css} from 'aphrodite';
import React, {useMemo} from 'react';

import {ActionOrigin} from 'constants/AnalyticsConstants';
import {BASE_FONT_PROXIMA, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';

import DataLayoutContext from './DataLayoutContext';
import DataLayoutSection from './DataLayoutSection';
import {DataLayout as DataLayoutType, LayoutEvent} from './DataLayoutTypes';

interface DataLayoutProps {
  actionOrigin: ActionOrigin;
  clientId: string;
  onEvents?: (events: Array<LayoutEvent>) => void;
  layout: DataLayoutType;
}

/**
 * Component for rendering data layouts.
 *
 * Data layouts are composed of sections, which are further composed of blocks. Blocks represent
 * a single piece of UI, and layouts define how to arrange those pieces of UI, and in the future
 * what types of interactions are possible across blocks.
 *
 * See DataLayoutTypes.ts for further details on the different types of sections and blocks.
 */
export default function DataLayout({actionOrigin, clientId, onEvents, layout}: DataLayoutProps): JSX.Element {
  const contextValue = useMemo(
    () => ({actionOrigin, clientId, onEvents}),
    [actionOrigin, clientId, onEvents]
  );

  return (
    <DataLayoutContext.Provider value={contextValue}>
      <div {...getTestProps({component: 'DataLayout', element: 'root'})}>
        <div className={css(styles.title)}>
          {layout.name}
          <Tag color={TagColor.BETA} size={TagSize.SMALL} text="BETA" />
        </div>
        {layout.sections.map((section, index) => (
          <DataLayoutSection layoutPath={['sections', index]} section={section} key={index} />
        ))}
      </div>
    </DataLayoutContext.Provider>
  );
}

const styles = StyleSheet.create({
  title: {
    ...BASE_FONT_PROXIMA,
    fontSize: 22,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    margin: '10px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
});
